import { useEffect, useState } from "react";
import { DownloadSimple, Phone } from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  return /iPhone|iPad|iPod/.test(ua) && !window.MSStream;
}

export default function InstallButton() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(() => isStandalone());
  const [iosHelpOpen, setIosHelpOpen] = useState(false);
  const [iosDevice] = useState(() => isIOS());

  useEffect(() => {
    const onBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onAppInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onAppInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onAppInstalled);
    };
  }, []);

  if (installed) return null;
  // On iOS, show button that opens manual instructions
  if (iosDevice) {
    return (
      <>
        <button
          data-testid="install-app-btn"
          onClick={() => setIosHelpOpen(true)}
          title="Instalar como app"
          className="hidden md:flex items-center gap-2 bg-[#FACC15] hover:bg-[#EAB308] text-black font-heading font-bold rounded-xl px-3 md:px-4 py-2.5 deck-tile transition-all"
        >
          <DownloadSimple size={16} weight="bold" />
          <span className="text-sm">Instalar</span>
        </button>
        <Dialog open={iosHelpOpen} onOpenChange={setIosHelpOpen}>
          <DialogContent className="bg-deck-sidebar border-white/10 text-white max-w-sm">
            <DialogHeader>
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl bg-[#FACC15] flex items-center justify-center deck-tile">
                  <Phone size={22} weight="fill" className="text-black" />
                </div>
                <div>
                  <DialogTitle className="text-white font-heading text-xl">Instala en iOS</DialogTitle>
                </div>
              </div>
              <DialogDescription className="text-white/60 font-mono text-xs leading-relaxed pt-2">
                En 2 pasos, LaunchPad queda como app en tu pantalla de inicio.
              </DialogDescription>
            </DialogHeader>
            <ol className="space-y-3 mt-2 text-sm">
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-[#2563EB] flex items-center justify-center text-white font-bold text-xs shrink-0">1</span>
                <span>Toca el botón <strong>Compartir</strong> en Safari (el cuadradito con flecha hacia arriba).</span>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-[#2563EB] flex items-center justify-center text-white font-bold text-xs shrink-0">2</span>
                <span>Elige <strong>"Añadir a pantalla de inicio"</strong> y luego <strong>Añadir</strong>.</span>
              </li>
            </ol>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // Android/Desktop Chrome/Edge
  if (!deferredPrompt) return null;

  const handleInstall = async () => {
    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") {
        setInstalled(true);
      }
      setDeferredPrompt(null);
    } catch {
      setDeferredPrompt(null);
    }
  };

  return (
    <button
      data-testid="install-app-btn"
      onClick={handleInstall}
      title="Instalar app"
      className="hidden md:flex items-center gap-2 bg-[#FACC15] hover:bg-[#EAB308] text-black font-heading font-bold rounded-xl px-3 md:px-4 py-2.5 deck-tile transition-all"
    >
      <DownloadSimple size={16} weight="bold" />
      <span className="text-sm">Instalar</span>
    </button>
  );
}
